// External component imports
import { Typography } from '@interstate/components/Typography';
import { Box } from '@interstate/components/Box';
import { Grid } from '@interstate/components/Grid';
import { useInterstateTheme } from '@interstate/components/InterstateThemeProvider';

// Constants and types
import type { AssessmentLevel, BinaryValues, ComplianceSections, StatusValue } from '../../shared/types';
import { AllowedAssessmentLevels, ComplianceStatus } from '../../shared/types';
import { ASSESSMENT_LEVEL_KEY, COMPLIANCE, FINAL_ASSESSMENT_KEY, SYNTHETIC_ID_VERIFICATION } from '../../shared/constants';

// Local style imports
import { useStyles } from '../ComplianceAccordian.style';
import { Separator } from '../../ComplianceForm.style';

// Local utility imports
import { getStatus, getSyntheticReasons } from '../../shared/utils';

// Common component imports
import { RiskSlider } from '../../../common/RiskSlider/RiskSlider';
import { useSelector } from 'react-redux';
import * as complianceSelectors from '@makemydeal/dr-dash-store/selectors/complianceSelectors';
import { StatusIcon } from '../../shared/StatusIcon';
import { Alert } from '@interstate/components/Alert';

export const SyntheticIdVerificationDetails = () => {
    const theme = useInterstateTheme();
    const classes = useStyles();
    const participantMatch = useSelector(complianceSelectors.getComplianceParticipantForShopper);

    const data = (participantMatch?.compliance ?? {}) as Partial<ComplianceSections>;
    const SyntheticIdData = data.syntheticIDVerification ?? {};

    const findings = Array.isArray(SyntheticIdData.findings) ? SyntheticIdData.findings : [];

    const finalAssesmentValue = findings.find((item: any) => item.name === FINAL_ASSESSMENT_KEY)?.value || '';
    const status = getStatus(SyntheticIdData.status, finalAssesmentValue as BinaryValues);

    const isPassOrFailStatus = status === ComplianceStatus.PASS || status === ComplianceStatus.FAIL;

    const syntheticIdReasons = getSyntheticReasons(findings);

    const message = SYNTHETIC_ID_VERIFICATION.MESSAGES[status as keyof typeof SYNTHETIC_ID_VERIFICATION.MESSAGES] || '';

    const assessmentLevelFinding = findings.find((f: any) => f.name === ASSESSMENT_LEVEL_KEY);
    const parsedLevel = assessmentLevelFinding ? Number(assessmentLevelFinding.value) : null;

    const fraudLevel = AllowedAssessmentLevels.includes(parsedLevel as AssessmentLevel) ? (parsedLevel as AssessmentLevel) : null;

    // Handle ERROR
    if (status === ComplianceStatus.ERROR) {
        return (
            <>
                <Alert data-testid="error-alert" title={COMPLIANCE.ERROR_ALERT.TITLE} type="error">
                    <span>{COMPLIANCE.ERROR_ALERT.DESCRIPTION}</span>
                </Alert>
                <Separator
                    isVisible={true}
                    theme={theme}
                    className="card_separator"
                    data-testid="card_separator"
                    aria-hidden="true"
                />
            </>
        );
    }

    return (
        <Box sx={{ pr: '1.5rem', pl: '1.8rem' }}>
            <Typography variant="body-sm">{message.details}</Typography>
            {fraudLevel !== null && isPassOrFailStatus && (
                <>
                    <Separator
                        isVisible={true}
                        theme={theme}
                        className="card_separator"
                        data-testid="card_separator"
                        aria-hidden="true"
                    ></Separator>
                    <Typography variant="body-sm">
                        {SYNTHETIC_ID_VERIFICATION.FRAUD_LEVEL_MESSAGE.getMessage(fraudLevel)}
                    </Typography>
                    <Separator
                        isVisible={false}
                        theme={theme}
                        className="card_separator"
                        data-testid="card_hidden_separator"
                        aria-hidden="true"
                    ></Separator>
                </>
            )}

            {fraudLevel !== null && isPassOrFailStatus && (
                <RiskSlider riskLevel={fraudLevel} leftLabel="Lowest risk" rightLabel="Highest risk" />
            )}
            {syntheticIdReasons.length > 0 && isPassOrFailStatus && (
                <>
                    <Separator
                        isVisible={false}
                        theme={theme}
                        className="card_separator"
                        data-testid="card_hidden_separator"
                        aria-hidden="true"
                    ></Separator>
                    <Typography variant="strong-sm">{COMPLIANCE.SECTION_TITLES.DETAILS}</Typography>
                    <Separator
                        isVisible={false}
                        theme={theme}
                        className="card_separator"
                        data-testid="card_hidden_separator"
                        aria-hidden="true"
                    ></Separator>
                    {syntheticIdReasons.map((reason) => (
                        <Grid
                            key={`doc-trust-reason-${reason.name}`}
                            className={classes.container}
                            data-testid={`doc-trust-reason-${reason.name}`}
                        >
                            <Grid xs="auto" className={classes.iconWrapper}>
                                <StatusIcon
                                    status={reason.status as StatusValue.Passed | StatusValue.Failed | StatusValue.Undetermined}
                                />
                            </Grid>
                            <Grid xs="auto" className={classes.textWrapper}>
                                <Typography variant="strong-sm">{reason.name}</Typography>
                                <Typography variant="body-sm">{reason.description}</Typography>
                            </Grid>
                        </Grid>
                    ))}
                </>
            )}
        </Box>
    );
};
